package com.shadowings.gero.data.lessons.chapter4

import com.shadowings.gero.data.lessons.inlineMeaning
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.TextTranslationContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_a_differenza_del_genitivo_qui_non_c_un_rapporto_di
import gero.composeapp.generated.resources.exp_content_esempio_padre_any_n_nil_padre_any_n_s_n_un
import gero.composeapp.generated.resources.exp_content_esempio_sacerdote_ptah_n_nil_sacerdote_ptah_n
import gero.composeapp.generated.resources.exp_content_nel_quiz_traduci_frasi_con_l_apposizione_mantenendo
import gero.composeapp.generated.resources.exp_content_nell_apposizione_due_nomi_vicini_indicano_la_stessa
import gero.composeapp.generated.resources.exp_content_ora_proviamo_a_riconoscere_e_tradurre_l_apposizione
import gero.composeapp.generated.resources.exp_content_questa_regola_ti_aiuta_a_leggere_subito_titoli_e_nomi
import gero.composeapp.generated.resources.exp_content_struttura_n_nome_con_articolo_nome_in_apposizione
import gero.composeapp.generated.resources.exp_title_apposizione
import gero.composeapp.generated.resources.exp_title_esempio_1
import gero.composeapp.generated.resources.exp_title_esempio_2
import gero.composeapp.generated.resources.exp_title_lezione_completata

fun getApposizioneContent(): List<IExplanation> {
    return listOf(
        ExplanationContent(
            title = Res.string.exp_title_apposizione,
            content = Res.string.exp_content_nell_apposizione_due_nomi_vicini_indicano_la_stessa,
            face = Faces.Happy
        ),
        ExplanationContent(
            title = Res.string.exp_title_apposizione,
            content = Res.string.exp_content_struttura_n_nome_con_articolo_nome_in_apposizione,
            face = Faces.Serious,
            composable = {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    MdCWord(
                        word = "M17-X1:I9-A1-G1-N35:Z4-A1",
                        rtl = false,
                        cart = false
                    )
                }
            }
        ),
        ExplanationContent(
            title = Res.string.exp_title_apposizione,
            content = Res.string.exp_content_questa_regola_ti_aiuta_a_leggere_subito_titoli_e_nomi,
            face = Faces.Idle
        ),
        ExplanationContent(
            title = Res.string.exp_title_apposizione,
            content = Res.string.exp_content_a_differenza_del_genitivo_qui_non_c_un_rapporto_di,
            face = Faces.Surprised
        ),
        ExplanationContent(
            title = Res.string.exp_title_esempio_1,
            content = Res.string.exp_content_esempio_padre_any_n_nil_padre_any_n_s_n_un,
            face = Faces.Happy,
            composable = {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    Row {
                        MdCWord(
                            word = "M23-X1:N35",
                            rtl = false,
                            cart = false
                        )
                        MdCWord(
                            word = "M17-Y5:N35-G43B-S34",
                            rtl = false,
                            cart = true
                        )
                    }
                }
            }
        ),
        ExplanationContent(
            title = Res.string.exp_title_esempio_2,
            content = Res.string.exp_content_esempio_sacerdote_ptah_n_nil_sacerdote_ptah_n,
            face = Faces.Surprised,
            composable = {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    Row {
                        MdCWord("M23-L2", rtl = false, cart = false)
                        MdCWord("V28-D36:Q3-M17-G43B-S34", rtl = false, cart = true)
                    }
                }
            }
        ),
        ExplanationContent(
            title = Res.string.exp_title_lezione_completata,
            content = Res.string.exp_content_nel_quiz_traduci_frasi_con_l_apposizione_mantenendo,
            face = Faces.Happy
        ),
        ExplanationContent(
            title = Res.string.exp_title_lezione_completata,
            content = Res.string.exp_content_ora_proviamo_a_riconoscere_e_tradurre_l_apposizione,
            face = Faces.Perfect
        )
    )
}

fun getApposizioneQuizContent(words: List<Word>, languageCode: String): List<IQuizContent> {
    val byId = words.associateBy { it.id.uppercase() }

    val appositionPairs = listOf(
        "PADRE" to "ANY",
        "MADRE" to "ITETI",
        "SCRIBA" to "DEN",
        "SACERDOTE" to "PTAH",
        "PASTORE" to "HA",
        "UOMO" to "SOKAR",
        "DONNA" to "TAURET",
        "TORO" to "AKER",
        "RE_DEL_BASSO_EGITTO" to "INEPU",
        "APICOLTORE" to "ANY",
        "MUCCA" to "ITETI",
        "GATTO" to "DEN"
    )

    return appositionPairs.mapNotNull { (headId, appositionId) ->
        val head = byId[headId] ?: return@mapNotNull null
        val apposition = byId[appositionId] ?: return@mapNotNull null

        TextTranslationContent(
            hieroglyphs = "${head.gardiner}-${apposition.gardiner}",
            correctPhrase = buildAppositionPhrase(head, apposition, languageCode),
            transliteration = "${head.reading} ${apposition.reading}"
        )
    }.shuffled().take(10)
}

private fun buildAppositionPhrase(head: Word, apposition: Word, languageCode: String): String {
    val article = head.getArticolo(languageCode).orEmpty().trim()
    val headNoun = head.inlineMeaning(languageCode, head.getMeaning(languageCode).split(",")[0].trim())
    val appositionNoun = apposition.inlineMeaning(languageCode, apposition.getMeaning(languageCode).split(",")[0].trim())

    val left = if (article.isNotBlank() && article != "-") {
        if (article.endsWith("'")) "$article$headNoun" else "$article $headNoun"
    } else {
        headNoun
    }

    return "$left, $appositionNoun"
}

